import { TAB_PAGES } from "@/lib/tabs/pageDescriptors";
import {
  buildId,
  encodeTranslatedTabTitle,
  makeTab,
  withTitleOverride,
} from "@/lib/tabs/registryCore";
import {
  decodeAdminLogsState,
  encodeAdminLogsState,
  getAdminLogsTabTitle,
  normalizeAdminLogsParams,
} from "@/lib/tabs/registryAdminLogs";

export const apiKeyCreateId = () => buildId("api-key", "create");
export const apiKeyCreateTab = () =>
  makeTab({
    descriptor: TAB_PAGES.ApiKeyCreatePage,
    idParts: ["api-key", "create"],
    title: "tabs.api_keys.create",
  });

export const apiKeyEditId = (clientId) => buildId("api-key", "edit", clientId);
export const apiKeyEditTab = (clientId) =>
  makeTab({
    descriptor: TAB_PAGES.ApiKeyEditPage,
    idParts: ["api-key", "edit", clientId],
    title: clientId,
    params: { clientId },
  });

export const adminExternalInvitesId = () =>
  buildId("admin", "external-invites");
export const adminExternalInvitesTab = (search) =>
  makeTab({
    descriptor: TAB_PAGES.AdminExternalInvitesPage,
    idParts: ["admin", "external-invites"],
    title: "tabs.admin.external_invites",
    params: { search },
  });

export const internalInvitesId = () => buildId("internal", "invites");
export const internalInvitesTab = (search, role) =>
  makeTab({
    descriptor: TAB_PAGES.InternalInvitesPage,
    idParts: ["internal", "invites"],
    title: "tabs.admin.internal_invites",
    params: { search, role },
  });

export const companyId = (companyIdValue) => buildId("company", companyIdValue);
export const companyTab = (companyIdValue, companyName) =>
  makeTab({
    descriptor: TAB_PAGES.AdminCompanyPage,
    idParts: ["company", companyIdValue],
    title: companyName,
    params: { companyId: companyIdValue },
  });

export const userCompanyId = (userIdValue, companyIdValue) =>
  buildId("user-company", userIdValue, companyIdValue);

export const userCompanyTab = (
  userIdValue,
  companyIdValue,
  companyName,
  firstName,
  lastName,
) =>
  makeTab({
    descriptor: TAB_PAGES.UserCompanyPage,
    idParts: ["user-company", userIdValue, companyIdValue],
    title: `${firstName} ${lastName} in ${companyName}`.trim(),
    params: { userId: userIdValue, companyId: companyIdValue },
  });

export const externalUserId = (userIdValue) =>
  buildId("user", "external", userIdValue);
export const internalUserId = (userIdValue) =>
  buildId("user", "internal", userIdValue);

export const externalUserTab = (userIdValue, firstName, lastName) =>
  makeTab({
    descriptor: TAB_PAGES.AdminExternalUserPage,
    idParts: ["user", "external", userIdValue],
    title: `${firstName} ${lastName}`.trim(),
    params: { userId: userIdValue },
  });

export const internalUserTab = (userIdValue, firstName, lastName) =>
  makeTab({
    descriptor: TAB_PAGES.AdminInternalUserPage,
    idParts: ["user", "internal", userIdValue],
    title: `${firstName} ${lastName}`.trim(),
    params: { userId: userIdValue },
  });

export const marketplacePartnerUserId = (userIdValue) =>
  buildId("user", "marketplace-partner", userIdValue);

export const marketplacePartnerUserTab = (
  userIdValue,
  firstName,
  lastName,
) =>
  makeTab({
    descriptor: TAB_PAGES.AdminMarketplacePartnerUserPage,
    idParts: ["user", "marketplace-partner", userIdValue],
    title: `${firstName} ${lastName}`.trim(),
    params: { userId: userIdValue },
  });

export const apiKeysId = () => buildId("api-keys");
export const apiKeysTab = () =>
  makeTab({
    descriptor: TAB_PAGES.ApiKeysPage,
    idParts: ["api-keys"],
    title: "tabs.api_keys.api_keys",
    params: {},
  });

export const companiesId = () => buildId("companies");
export const companiesTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminCompaniesPage,
    idParts: ["companies"],
    title: "tabs.admin.companies",
    params: {},
  });

export const translationsId = () => buildId("translations");
export const translationsTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminTranslationsPage,
    idParts: ["translations"],
    title: "tabs.admin.translations",
    params: {},
  });

export const externalUsersId = () => buildId("users", "external");
export const externalUsersTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminExternalUsersPage,
    idParts: ["users", "external"],
    title: "tabs.admin.external_users",
    params: {},
  });

export const internalUsersId = () => buildId("users", "internal");
export const internalUsersTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminInternalUsersPage,
    idParts: ["users", "internal"],
    title: "tabs.admin.internal_users",
    params: {},
  });

export const adminMarketplaceOverviewId = () => buildId("admin", "marketplace");
export const adminMarketplaceOverviewTab = () =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceOverviewPage,
    idParts: ["admin", "marketplace"],
    title: "tabs.admin.marketplace",
    params: {},
  });

export const adminNewsOverviewId = () => buildId("admin", "news");
export const adminNewsOverviewTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminNewsOverviewPage,
    idParts: ["admin", "news"],
    title: "tabs.admin.news",
    params: {},
  });

export const adminNewsArticleCreateId = () =>
  buildId("admin", "news", "article", "create");
export const adminNewsArticleCreateTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminNewsArticleEditPage,
    idParts: ["admin", "news", "article", "create"],
    title: "tabs.admin.news_article_create",
    params: {},
  });

export const adminNewsArticleCopyId = (articleId) =>
  buildId("admin", "news", "article", "copy", articleId);
export const adminNewsArticleCopyTab = (articleId, languageCode) =>
  makeTab({
    descriptor: TAB_PAGES.AdminNewsArticleEditPage,
    idParts: ["admin", "news", "article", "copy", articleId],
    title: "tabs.admin.news_article_create",
    params: {
      copyFromArticleId: articleId,
      languageCode,
    },
  });

export const adminNewsArticleEditId = (articleId) =>
  buildId("admin", "news", "article", "edit", articleId);
export const adminNewsArticleEditTab = (
  articleId,
  articleTitle,
  languageCode,
  options = {},
) =>
  makeTab({
    descriptor: TAB_PAGES.AdminNewsArticleEditPage,
    idParts: ["admin", "news", "article", "edit", articleId],
    title: articleTitle || "tabs.admin.news_article_edit",
    params: {
      articleId,
      languageCode,
      unarchiveOnSave: !!options.unarchiveOnSave,
    },
  });

export const adminNewsFacetCreateId = () =>
  buildId("admin", "news", "facet", "create");
export const adminNewsFacetCreateTab = () =>
  makeTab({
    descriptor: TAB_PAGES.AdminNewsFacetEditPage,
    idParts: ["admin", "news", "facet", "create"],
    title: "tabs.admin.news_facet_create",
    params: {},
  });

export const adminNewsFacetEditId = (facetId) =>
  buildId("admin", "news", "facet", "edit", facetId);
export const adminNewsFacetEditTab = (facetId, facetName, languageCode) =>
  makeTab({
    descriptor: TAB_PAGES.AdminNewsFacetEditPage,
    idParts: ["admin", "news", "facet", "edit", facetId],
    title: facetName || "tabs.admin.news_facet_edit",
    params: { facetId, languageCode },
  });

export const adminMarketplaceSectionCreateId = () =>
  buildId("admin", "marketplace", "section", "create");
export const adminMarketplaceSectionCreateTab = () =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceSectionEditPage,
    idParts: ["admin", "marketplace", "section", "create"],
    title: "tabs.admin.marketplace_section_create",
    params: {},
  });

export const adminMarketplaceSectionEditId = (sectionId) =>
  buildId("admin", "marketplace", "section", "edit", sectionId);
export const adminMarketplaceSectionEditTab = (sectionId, sectionKey) =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceSectionEditPage,
    idParts: ["admin", "marketplace", "section", "edit", sectionId],
    title: sectionKey || sectionId,
    params: { sectionId },
  });

export const adminMarketplaceServiceCreateId = () =>
  buildId("admin", "marketplace", "service", "create");
export const adminMarketplaceServiceCreateTab = (sectionId = "") =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceServiceEditPage,
    idParts: ["admin", "marketplace", "service", "create"],
    title: "tabs.admin.marketplace_service_create",
    params: { sectionId },
  });

export const adminMarketplaceServiceEditId = (serviceId) =>
  buildId("admin", "marketplace", "service", "edit", serviceId);
export const adminMarketplaceServiceEditTab = (serviceId, serviceKey) =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceServiceEditPage,
    idParts: ["admin", "marketplace", "service", "edit", serviceId],
    title: serviceKey || serviceId,
    params: { serviceId },
  });

export const adminMarketplacePartnerCreateId = () =>
  buildId("admin", "marketplace", "partner", "create");
export const adminMarketplacePartnerCreateTab = (serviceId = "") =>
  makeTab({
    descriptor: TAB_PAGES.MarketplacePartnerEditPage,
    idParts: ["admin", "marketplace", "partner", "create"],
    title: "tabs.admin.marketplace_partner_create",
    params: { serviceId },
  });

export const adminMarketplacePartnerEditId = (partnerId) =>
  buildId("admin", "marketplace", "partner", "edit", partnerId);
export const adminMarketplacePartnerEditTab = (partnerId, partnerKey) =>
  makeTab({
    descriptor: TAB_PAGES.MarketplacePartnerEditPage,
    idParts: ["admin", "marketplace", "partner", "edit", partnerId],
    title: partnerKey || partnerId,
    params: { partnerId },
  });

export const adminMarketplacePartnerPageEditId = (partnerId) =>
  buildId("admin", "marketplace", "partner", "page", partnerId);
export const adminMarketplacePartnerPageEditTab = (partnerId, partnerKey) =>
  makeTab({
    descriptor: TAB_PAGES.MarketplacePartnerPageEditPage,
    idParts: ["admin", "marketplace", "partner", "page", partnerId],
    title: encodeTranslatedTabTitle({
      key: "tabs.admin.marketplace_partner_page",
      params: { partnerLabel: partnerKey || partnerId },
    }),
    params: { partnerId },
  });

export const adminMarketplaceFacetCreateId = () =>
  buildId("admin", "marketplace", "facet", "create");
export const adminMarketplaceFacetCreateTab = () =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceFacetEditPage,
    idParts: ["admin", "marketplace", "facet", "create"],
    title: "tabs.admin.marketplace_facet_create",
    params: {},
  });

export const adminMarketplaceFacetEditId = (facetId) =>
  buildId("admin", "marketplace", "facet", "edit", facetId);
export const adminMarketplaceFacetEditTab = (facetId, facetKey) =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceFacetEditPage,
    idParts: ["admin", "marketplace", "facet", "edit", facetId],
    title: facetKey || facetId,
    params: { facetId },
  });

export const adminMarketplaceTranslationsId = () =>
  buildId("admin", "marketplace", "translations");
export const adminMarketplaceTranslationsTab = () =>
  makeTab({
    descriptor: TAB_PAGES.MarketplaceTranslationsPage,
    idParts: ["admin", "marketplace", "translations"],
    title: "tabs.admin.marketplace_translations",
    params: {},
  });

export const adminLogsId = (params = {}) => {
  const encodedState = encodeAdminLogsState(params);
  return encodedState
    ? buildId("admin", "logs", encodedState)
    : buildId("admin", "logs");
};

export const adminLogsTab = (params = {}, title = null) => {
  const normalized = normalizeAdminLogsParams(params);
  const encodedState = encodeAdminLogsState(normalized);

  return makeTab({
    descriptor: TAB_PAGES.AdminLogsPage,
    idParts: encodedState ? ["admin", "logs", encodedState] : ["admin", "logs"],
    title: title || getAdminLogsTabTitle(normalized),
    params: normalized,
  });
};

const staticTabBuilders = {
  [apiKeysId()]: () => apiKeysTab(),
  [companiesId()]: () => companiesTab(),
  [translationsId()]: () => translationsTab(),
  [adminMarketplaceOverviewId()]: () => adminMarketplaceOverviewTab(),
  [adminNewsOverviewId()]: () => adminNewsOverviewTab(),
  [adminNewsArticleCreateId()]: () => adminNewsArticleCreateTab(),
  [adminNewsFacetCreateId()]: () => adminNewsFacetCreateTab(),
  [adminMarketplaceSectionCreateId()]: () => adminMarketplaceSectionCreateTab(),
  [adminMarketplaceServiceCreateId()]: () => adminMarketplaceServiceCreateTab(),
  [adminMarketplacePartnerCreateId()]: () => adminMarketplacePartnerCreateTab(),
  [adminMarketplaceFacetCreateId()]: () => adminMarketplaceFacetCreateTab(),
  [adminMarketplaceTranslationsId()]: () => adminMarketplaceTranslationsTab(),
  [externalUsersId()]: () => externalUsersTab(),
  [internalUsersId()]: () => internalUsersTab(),
  [adminLogsId()]: () => adminLogsTab(),
  [adminExternalInvitesId()]: () => adminExternalInvitesTab(),
  [internalInvitesId()]: () => internalInvitesTab(undefined, undefined),
  [apiKeyCreateId()]: () => apiKeyCreateTab(),
};

const dynamicTabResolvers = [
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "news" &&
      parts[2] === "facet" &&
      parts[3] === "edit" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminNewsFacetEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "news" &&
      parts[2] === "article" &&
      parts[3] === "copy" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminNewsArticleCopyTab(parts[4]),
        titleOverride,
        "tabs.admin.news_article_create",
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "news" &&
      parts[2] === "article" &&
      parts[3] === "edit" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminNewsArticleEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "marketplace" &&
      parts[2] === "section" &&
      parts[3] === "edit" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminMarketplaceSectionEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "marketplace" &&
      parts[2] === "service" &&
      parts[3] === "edit" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminMarketplaceServiceEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "marketplace" &&
      parts[2] === "partner" &&
      parts[3] === "edit" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminMarketplacePartnerEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "marketplace" &&
      parts[2] === "partner" &&
      parts[3] === "page" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminMarketplacePartnerPageEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "admin" &&
      parts[1] === "marketplace" &&
      parts[2] === "facet" &&
      parts[3] === "edit" &&
      parts[4],
    create: (parts, titleOverride) =>
      withTitleOverride(
        adminMarketplaceFacetEditTab(parts[4], titleOverride || parts[4]),
        null,
        titleOverride || parts[4],
      ),
  },
  {
    matches: (parts) => parts[0] === "admin" && parts[1] === "logs",
    create: (parts, titleOverride) => {
      const params = parts[2] ? decodeAdminLogsState(parts[2]) : {};
      return withTitleOverride(
        adminLogsTab(params, titleOverride || getAdminLogsTabTitle(params)),
        null,
        titleOverride || getAdminLogsTabTitle(params),
      );
    },
  },
  {
    matches: (parts) => parts[0] === "api-key" && parts[1] === "edit" && parts[2],
    create: (parts, titleOverride) =>
      withTitleOverride(apiKeyEditTab(parts[2]), titleOverride, parts[2]),
  },
  {
    matches: (parts) => parts[0] === "user" && parts[1] === "external" && parts[2],
    create: (parts, titleOverride) =>
      withTitleOverride(
        externalUserTab(parts[2], "", ""),
        titleOverride,
        parts[2],
      ),
  },
  {
    matches: (parts) => parts[0] === "user" && parts[1] === "internal" && parts[2],
    create: (parts, titleOverride) =>
      withTitleOverride(
        internalUserTab(parts[2], "", ""),
        titleOverride,
        parts[2],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "user" &&
      parts[1] === "marketplace-partner" &&
      parts[2],
    create: (parts, titleOverride) =>
      withTitleOverride(
        marketplacePartnerUserTab(parts[2], "", ""),
        titleOverride,
        parts[2],
      ),
  },
  {
    matches: (parts) => parts[0] === "company" && parts.length > 1,
    create: (parts, titleOverride) =>
      withTitleOverride(
        companyTab(parts[1], titleOverride || parts[1]),
        null,
        parts[1],
      ),
  },
  {
    matches: (parts) => parts[0] === "user-company" && parts[1] && parts[2],
    create: (parts, titleOverride) =>
      withTitleOverride(
        userCompanyTab(parts[1], parts[2], parts[2], parts[1], ""),
        titleOverride,
        `${parts[1]} in ${parts[2]}`,
      ),
  },
];

export default {
  dynamicTabResolvers,
  staticTabBuilders,
};
